import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Alert, CircularProgress, Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface Props {
  open: boolean;
  userId: string | null;
  onClose: () => void;
}

export function ResetPasswordDialog({ open, userId, onClose }: Props) {
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    if (open) { setPassword(""); setConfirm(""); }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => usersApi.resetPassword(userId!, password),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
  });

  const err = (() => {
    if (!password) return "Password is required";
    if (password.length < 8) return "At least 8 characters";
    if (!/[A-Z]/.test(password)) return "Needs an uppercase letter";
    if (!/[a-z]/.test(password)) return "Needs a lowercase letter";
    if (!/[0-9]/.test(password)) return "Needs a digit";
    if (password !== confirm) return "Passwords do not match";
    return null;
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset password</DialogTitle>
      <DialogContent dividers>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          All active refresh tokens for this user will be revoked. They'll need to log in again.
        </Alert>
        <Stack spacing={2}>
          <TextField label="New password *" type="password" size="small" fullWidth
            value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
          <TextField label="Confirm new password *" type="password" size="small" fullWidth
            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {err && <Alert severity="info">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={() => mutation.mutate()}
          disabled={!!err || mutation.isPending || !userId}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
          Reset password
        </Button>
      </DialogActions>
    </Dialog>
  );
}
